import { logicIndexToSpriteIndex } from 'helpers/sharedHelpers';
import { getRoomsAt, tileShouldBeCheckered } from 'helpers/mapHelpers';
import { getRectangleOfPlayerTiles } from 'helpers/levelHelpers';
import {
  LOGIC_DOOR,
  RED_PIECES,
  BLUE_PIECES,
  COLORLESS_PIECES,
  ITEMS,
  KEY,
  AMULET,
  WARP,
  WALKABLE,
  TORCH
} from 'helpers/constants';

import AbilityButton from 'ui/abilityButton';

import ALL_ITEMS from 'items/all_items';
import { SINGLE_TILE_ITEMS, TELEPORT_DARK, TELEPORT_LIGHT } from './constants';

export function unitIndexFromKey(key) {
  let result = null;
  // console.log('converting tile index to unit sprite:', key);
  switch (key) {
    case 'Pf':
      result = 138;
      break;
    case 'Pp':
      result = 126;
      break;
    case 'Hp':
      result = 127;
      break;
    case 'Bp':
      result = 128;
      break;
    case 'Rp':
      result = 129;
      break;
    case 'Qp':
      result = 130;
      break;
    case 'Kp':
      result = 131;
      break;

    case 'Pp_':
      result = 132;
      break;
    case 'Hp_':
      result = 133;
      break;
    case 'Bp_':
      result = 134;
      break;
    case 'Rp_':
      result = 135;
      break;
    case 'Qp_':
      result = 136;
      break;
    case 'Kp_':
      result = 137;
      break;
    case 'P':
      result = 144;
      break;
    case 'H':
      result = 145;
      break;
    case 'B':
      result = 146;
      break;
    case 'R':
      result = 147;
      break;
    case 'Q':
      result = 148;
      break;
    case 'K':
      result = 149;
      break;

    case 'P_':
      result = 150;
      break;
    case 'H_':
      result = 151;
      break;
    case 'B_':
      result = 152;
      break;
    case 'R_':
      result = 153;
      break;
    case 'Q_':
      result = 154;
      break;
    case 'K_':
      result = 155;
      break;
    default:
      result = -1;
      break;
  }
  return result;
}

export function getMapPieceData(map, layerIndex = 4) {
  const data = [];
  const mapLayer = map.layers[layerIndex].data;
  const battleZone = (map.scene.inZone && map.scene.inZone.rect) || {
    x: 0,
    y: 0,
    w: mapLayer[0].length,
    h: mapLayer.length
  };
  for (let y = battleZone.y; y < battleZone.y + battleZone.h; y++) {
    for (let x = battleZone.x; x < battleZone.x + battleZone.w; x++) {
      const tile = mapLayer[y][x];
      data.push(tile.index);
    }
  }
  return data;
}

function getDirectionOffset(direction) {
  switch (direction) {
    case 'up':
      return { x: 0, y: 32 };
    case 'left':
      return { x: 32, y: 0 };
    case 'right':
      return { x: -32, y: 0 };
    default:
    case 'down':
      return { x: 0, y: -32 };
  }
}

export function storyAnimateOutPiece(
  scene,
  getPiece,
  direction,
  speed,
  callback
) {
  const piece = getPiece();
  if (!piece || !piece.tile) {
    callback && callback();
    return;
  }
  let origin = { x: piece.tile.pixelX + 48, y: piece.tile.pixelY + 32 };
  const directionOffset = getDirectionOffset(direction);
  let animDestination = {
    x: origin.x - directionOffset.x,
    y: origin.y - directionOffset.y
  };
  scene.map.removeTileAt(
    piece.tile.x,
    piece.tile.y,
    false,
    false,
    'logic_layer'
  );

  scene.tweens.add({
    targets: piece.parentContainer,
    x: animDestination.x,
    y: animDestination.y,
    alpha: 0,
    ease: 'Quad.easeIn',
    duration: speed,
    onComplete: () => {
      piece.tile.index = -1;
      piece.tile.occupant = null;
      piece.tile = null;
      piece.destroy();
      callback && callback();
    }
  });
}

export function addPieceSpriteToBoard(
  scene,
  tile,
  equippedItem,
  pieceIndex,
  animateDefault = false,
  cutscene = false,
  direction = 'down',
  speed = 300,
  callback = () => {},
  pieceCount
) {
  if (!tile) {
    callback && callback(pieceCount);
    return null;
  }
  const alreadyThereTile = scene.map.getTileAt(
    tile.x,
    tile.y,
    true,
    'below_player'
  );
  const alreadyThere =
    alreadyThereTile && alreadyThereTile.index === pieceIndex;
  const animateIfNotAlreadyThere = animateDefault ? !alreadyThere : false;
  const animate = animateDefault ? animateIfNotAlreadyThere : false;
  let alpha = 1;
  if (scene.foundRooms) {
    let pieceRooms = getRoomsAt(tile.x, tile.y, scene.rooms);
    alpha = 0;
    pieceRooms.forEach(roomIndex => {
      if (scene.foundRooms.has(roomIndex)) {
        alpha = 0.5;
      }
      if (
        scene.currentActiveRooms &&
        scene.currentActiveRooms.roomIndexes &&
        scene.currentActiveRooms.roomIndexes.includes(roomIndex)
      ) {
        alpha = 1;
      }
    });
  }

  if (cutscene || animate) {
    alpha = 0;
  }
  if (pieceIndex === KEY) {
    // placing key - reset registry
    scene.game.registry.set('hasKey', false);
  }
  let isPiece = BLUE_PIECES.concat(RED_PIECES, COLORLESS_PIECES).includes(
    pieceIndex
  );
  let isItem = ITEMS.includes(pieceIndex);
  let pieceSpriteIndex;
  let spriteName = '2020_units';
  let itemOffset = 0;
  if (isPiece) {
    pieceSpriteIndex = logicIndexToSpriteIndex(pieceIndex);
  } else if (isItem) {
    if (SINGLE_TILE_ITEMS.includes(pieceIndex)) {
      spriteName = 'master_atlas';
      pieceSpriteIndex = pieceIndex - 1;
      itemOffset = 16;
    } else {
      if (pieceIndex === AMULET) {
        pieceSpriteIndex = 287;
      } else {
        pieceSpriteIndex = logicIndexToSpriteIndex(pieceIndex);
      }
    }
  }
  let origin = {
    x: tile.pixelX + 48,
    y: tile.pixelY + 32 + itemOffset - (tile.tileHeightOffset || 0)
  };
  const directionOffset = getDirectionOffset(direction);
  let animOrigin = {
    x: origin.x + directionOffset.x,
    y: origin.y + directionOffset.y
  };
  let placement = animate ? animOrigin : origin;
  let pieceContainer = scene.add.container(placement.x, placement.y);
  let piece = scene.add.sprite(0, 0, spriteName, pieceSpriteIndex);
  pieceContainer.add(piece);
  pieceContainer.setDepth(4);
  pieceContainer.setAlpha(alpha);
  tile.occupant = piece;
  scene.pieces.add(piece);
  piece.tile = tile;
  pieceContainer.setData('type', isPiece ? 'piece' : 'item');

  if (isPiece) {
    piece.tile.index = pieceIndex;
    if (RED_PIECES.includes(pieceIndex)) {
      piece.color = 'red';
    } else if (BLUE_PIECES.includes(pieceIndex)) {
      piece.color = 'blue';
    } else {
      // TODO - just making colorless pieces red for now...
      piece.color = 'red';
    }
    piece.setInteractive({ pixelPerfect: true });
    // add equipped item
    if (equippedItem) {
      piece.itemID = equippedItem.id;
      const abilityButton = AbilityButton(scene, tile, piece, equippedItem);
      pieceContainer.add(abilityButton);
      pieceContainer.bringToTop(abilityButton);
      if (equippedItem.init) {
        equippedItem.init(scene, tile, piece);
        if (equippedItem.charges) {
          piece.itemCharges = equippedItem.charges;
        }
      }
    }
  }
  if (animate) {
    // let duration = 350 + 50 * (tile.y % 20) + 50 * (tile.x % 20);
    let duration = 350;
    let ease = 'Expo.easeIn';
    if (cutscene) {
      duration = speed;
      ease = 'Quad.easeIn';
    }

    scene.tweens.add({
      targets: piece.parentContainer,
      x: origin.x,
      y: origin.y,
      alpha: 1,
      ease,
      duration,
      delay: pieceCount ? pieceCount * 400 : 0,
      onComplete: () => {
        const pieceStillLive =
          piece &&
          piece.scene &&
          piece.scene.sys &&
          !piece.destroyed &&
          piece.active !== false;
        if (
          pieceStillLive &&
          piece.color === 'blue' &&
          scene.scene.key === 'WorldScene'
        ) {
          scene.cameras.main.shake(100, { x: 0, y: 0.0015 });
        }
        callback(pieceCount);
      }
    });
    return piece;
  } else {
    callback(pieceCount);
    return piece;
  }
}

export const buildLevelBackground = scene => {
  let { levelData } = scene;
  let levelHeight = levelData.lvlData.ground.length;
  let levelWidth = levelData.lvlData.ground[0].length;
  let topLeft = { x: 32, y: 32 };
  // build a chcekboard graphic to hold the place behind a level
  if (scene.gridBg) {
    scene.gridBg.destroy();
  }
  scene.gridBg = scene.add.graphics();
  //checkboard pattern with colors: Dark tiles: #323232, Light tiles: #363636

  scene.gridBg.fillStyle(0x323232, 1);
  scene.gridBg.fillRect(
    topLeft.x,
    topLeft.y,
    levelWidth * 32,
    levelHeight * 32
  );

  for (let i = 0; i < levelWidth; i++) {
    for (let j = 0; j < levelHeight; j++) {
      if ((i % 2 === 0 && j % 2 !== 0) || (i % 2 !== 0 && j % 2 === 0)) {
        scene.gridBg.fillStyle(0x363636, 1);
        scene.gridBg.fillRect(topLeft.x + i * 32, topLeft.y + j * 32, 32, 32);
      }
    }
  }
};

export const buildCastle = (scene, options) => {
  buildLayers(scene);
  populateLayers(scene, { ...options, isCastle: true });
};

export const buildLevel = (
  scene,
  storyReplay,
  logicAlpha,
  selectedParty,
  dontCheckerBoard
) => {
  const shouldSkipCheckerBoard =
    typeof dontCheckerBoard === 'boolean'
      ? dontCheckerBoard
      : !!scene?.levelData?.dontCheckerBoard;

  buildLayers(scene, { logicAlpha });
  populateLayers(scene, {
    storyReplay,
    selectedParty,
    dontCheckerBoard: shouldSkipCheckerBoard
  });
};

export const buildWorld = scene => {
  buildLayers(scene);
  populateLayers(scene, { isWorld: true });
};

function buildLayers(scene, options = {}) {
  let { levelData } = scene;
  let levelHeight = levelData.lvlData.ground.length;
  let levelWidth = levelData.lvlData.ground[0].length;
  let topLeft = { x: 32, y: 32 };
  let { logicAlpha } = options;
  scene.map = scene.make.tilemap({
    tileWidth: 32,
    tileHeight: 32,
    width: levelWidth,
    height: levelHeight
  });

  var allTiles = scene.map.addTilesetImage('master_atlas');
  allTiles.firstgid = 1;

  scene.groundLayer = scene.map.createBlankDynamicLayer(
    'ground_layer',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.groundLayer.setDepth(1);

  scene.belowPlayer = scene.map.createBlankDynamicLayer(
    'below_player',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.belowPlayer.setDepth(2);

  scene.decorLayer = scene.map.createBlankDynamicLayer(
    'decor_layer',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.decorLayer.setDepth(3);

  scene.abovePlayer = scene.map.createBlankDynamicLayer(
    'above_player',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.abovePlayer.setDepth(5);

  scene.logicLayer = scene.map.createBlankDynamicLayer(
    'logic_layer',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.logicLayer.setDepth(6);
  scene.logicLayer.setAlpha(logicAlpha || 0);

  scene.highlightLayer = scene.map.createBlankDynamicLayer(
    'highlight_layer',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.highlightLayer.setDepth(4);

  scene.shadeLayer = scene.map.createBlankDynamicLayer(
    'shade_layer',
    allTiles,
    topLeft.x,
    topLeft.y
  );
  scene.shadeLayer.setDepth(7);

  scene.map.setBaseTileSize(32, 32);
}

export const WATER_TILES = [397, 398, 399];

export function populateLayers(scene, options) {
  let { levelData } = scene;
  let { isWorld, dontCheckerBoard } = options;
  //ground layer
  levelData.lvlData.ground.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      if (tileKey !== -1) {
        let tile = tileKey;
        if (
          !isWorld &&
          tileShouldBeCheckered(rowIndex, colIndex) &&
          !dontCheckerBoard
        ) {
          tile += 1;
        }
        scene.map.putTileAt(tile, x, y, true, 'ground_layer');
      }
    });
  });

  //below player layer
  levelData.lvlData.belowPlayer.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      const tile = scene.map.putTileAt(tileKey, x, y, true, 'below_player');

      if (isWorld && WATER_TILES.includes(tileKey)) {
        if (!scene.waterTiles) {
          scene.waterTiles = [];
        }
        scene.waterTiles.push(tile);
      }
    });
  });

  //decor layer
  levelData.lvlData.decor.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      const tile = scene.map.putTileAt(tileKey, x, y, true, 'decor_layer');
      if (tileKey === TORCH) {
        tile.torch = scene.add
          .sprite((x + 1.5) * 32, (y + 1.5) * 32, 'anim_torch')
          .setDepth(5);
        scene.animations.add(tile.torch);
        const config = {
          key: 'torch',
          frames: scene.anims.generateFrameNumbers('anim_torch'),
          frameRate: 3,
          yoyo: false,
          repeat: -1
        };

        scene.anims.create(config);
        tile.torch.anims.load('torch');
        tile.torch.anims.play('torch');
      }
    });
  });

  //above player layer
  levelData.lvlData.abovePlayer.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      scene.map.putTileAt(tileKey, x, y, true, 'above_player');
    });
  });

  //logic layer
  populateLogicLayer(
    scene,
    levelData.lvlData.logic,
    levelData.equippedItems,
    options
  );
}

export function populateLogicLayer(scene, layerData, equippedItems, options) {
  let {
    isWorld,
    isCastle,
    storyReplay,
    custscene,
    origin,
    skipAnimate,
    selectedParty,
    entryTile,
    piecesFinishedAnimating
  } = options;
  const { freeplayParty } = scene;
  const remainingSelectedParty = selectedParty && selectedParty.slice();
  let doorIndex = 0;
  const warps = [];
  scene.hasExit = false;
  origin = origin || { x: 0, y: 0 };
  //logic layer
  let pieceCount = 0;
  layerData.forEach((row, colIndex) => {
    row.forEach((tileKey, rowIndex) => {
      let y = colIndex;
      let x = rowIndex;

      let tile;

      //don't place the player's leader piece which is already on the board.
      const entrantSpot =
        entryTile &&
        entryTile.x === origin.x + x &&
        entryTile.y === origin.y + y;
      if (entrantSpot && !freeplayParty) {
        return;
      }

      const isPiece = [
        ...RED_PIECES,
        ...BLUE_PIECES,
        ...COLORLESS_PIECES
      ].includes(tileKey);
      const isBluePiece = BLUE_PIECES.includes(tileKey);
      const isItem = ITEMS.includes(tileKey);
      if (!storyReplay || (storyReplay && !isPiece && !isItem)) {
        let partyKey;
        let selectedMember;
        // console.log(
        //   'placing tile...',
        //   storyReplay,
        //   tileKey,
        //   isBluePiece,
        //   freeplayParty
        // );
        if (
          isBluePiece &&
          remainingSelectedParty &&
          remainingSelectedParty.length > 0 &&
          !freeplayParty
        ) {
          // find the tileKey as a rank in the selectedParty, and use the party member's key - could be a character key, not the rank key
          // when party is greater than requirements, the selectedParty will not be the full party
          // selectedParty will onyl be provided during story matches
          const selectedIndex = remainingSelectedParty.findIndex(member => {
            const rankOrigin = BLUE_PIECES[0];
            const rank = tileKey - rankOrigin;
            return member.rank === rank;
          });
          if (selectedIndex !== -1) {
            //remove the found member from the remaining list...
            selectedMember = remainingSelectedParty[selectedIndex];
            remainingSelectedParty.splice(selectedIndex, 1);
            // set partyKey to the party member's tileKey
            partyKey = selectedMember.tileKey;
          }
        } else if (isBluePiece && freeplayParty) {
          const { rectangle } = getRectangleOfPlayerTiles(scene.freeplayMap);
          // console.log(
          //   'placing units from freeplay party:',
          //   freeplayParty,
          //   x,
          //   y,
          //   origin.x,
          //   origin.y,
          //   rectangle,
          //   scene.freeplayMap
          // );

          // freeplayParty is the full party, and the selectedMember is the party member at the same index as the tileKey
          selectedMember = freeplayParty.find(freeplayPartyMember => {
            return (
              freeplayPartyMember.x + rectangle.x === x &&
              freeplayPartyMember.y + rectangle.y === y
            );
          });
          if (selectedMember) {
            const partyMember = remainingSelectedParty.find(
              p => p.id === parseInt(selectedMember.id, 10)
            );
            partyKey = partyMember.tileKey;
            selectedMember.itemID = partyMember.itemID;
          }
          // console.log(
          //   'partyKey:',
          //   remainingSelectedParty,
          //   selectedMember,
          //   partyKey
          // );
        }
        tile = scene.map.putTileAt(
          partyKey || tileKey,
          origin.x + x,
          origin.y + y,
          true,
          'logic_layer'
        );
        tile.tileHeightOffset = 0;
        const layerIndexes = getIndexesOnEveryLayerAt(scene, tile.x, tile.y);
        if (
          layerIndexes.includes(TELEPORT_DARK) ||
          layerIndexes.includes(TELEPORT_LIGHT)
        ) {
          tile.teleport = true;
          tile.tileHeightOffset = 3;
        }
        if (!isWorld && tileKey === LOGIC_DOOR) {
          scene.hasExit = true;
          tile.goal = true;
          tile.doorIndex = doorIndex;
          doorIndex += 1;
        }
        if (tileKey === WARP) {
          warps.push(tile);
        }
        if (tileKey === WALKABLE) {
          tile.interactable = true;
        }
        let equippedItem;
        // equippedItems from the level data
        // applies to enemies in story levels
        // applies to all pieces in non-story levels
        if (equippedItems && equippedItems.length) {
          let equippedItemData = equippedItems.find(item => {
            return item.x === x && item.y === y;
          });
          if (equippedItemData) {
            equippedItem = ALL_ITEMS[equippedItemData.item];
          }
        }
        //in story mode, for the player, check the selectPiece from the party above
        // this path is also used for freeplay replays using whatever item is equipped form the party planning edit loadout screen
        if (selectedMember) {
          equippedItem = ALL_ITEMS[selectedMember.itemID];
        }
        const inWorldNotBattling = isWorld && !scene.battlingInWorld;
        if (isPiece || isItem) {
          const nonAnimatedPiece = addPieceSpriteToBoard(
            scene,
            tile,
            equippedItem,
            partyKey || tileKey,
            !isCastle && !skipAnimate && !inWorldNotBattling,
            custscene,
            null,
            null,
            pieceNumber => {
              // pieceNumber is the count at the time this piece was started,
              // but by time we get here, all the pieces have started, so we
              // can use it and pieceCount as a progress/total.
              if (pieceNumber + 1 === pieceCount) {
                piecesFinishedAnimating && piecesFinishedAnimating();
              }
            },
            pieceCount
          );
          if (!nonAnimatedPiece) {
            pieceCount++;
          }
        }
      }
    });
  });
  if (pieceCount === 0 && piecesFinishedAnimating) {
    piecesFinishedAnimating();
  }

  warps.forEach((tile, index) => {
    tile.warp = true;
    tile.warpIndex = index;
    let warpToTile = index ? warps[0] : warps[1];
    tile.warpTo = { x: origin.x + warpToTile.x, y: origin.y + warpToTile.y };
  });
}

const getIndexesOnEveryLayerAt = (scene, x, y) => {
  return scene.map.layers.map(layer => {
    return scene.map.getTileAt(x, y, true, layer.name).index;
  });
};
